/* Creative Lab ASCII pulse: paired sweeps, pointer response, and a quiet loop. */
(() => {
  if (customElements.get('product-stock-signal')) return;

  const BURST = 560;
  const BEAT_GAP = 160;
  const RELAX = 240;
  const COOLDOWN = 4000;
  const ENTRANCE = 220;
  const CYCLE = BURST * 2 + BEAT_GAP + RELAX;

  class ProductStockSignal extends HTMLElement {
    connectedCallback() {
      this.dispose?.();
      let timer;
      let visible = false;
      let disposed = false;
      let nextBurstAt = 0;
      let pending = false;
      let lastInteraction = -Infinity;
      let pointerX = 0;
      let pointerY = 0;
      // The visible icon and message form one hover area, without adding a button.
      const surface = this.closest('.alert-area__item') || this;
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const runnable = () => !disposed && visible && !document.hidden && !motion.matches;
      const phase = name => { this.dataset.signalPhase = name; };
      const stop = () => { clearTimeout(timer); timer = undefined; };
      const later = (callback, delay) => {
        stop();
        timer = setTimeout(() => {
          timer = undefined;
          if (runnable()) callback();
          else sync();
        }, delay);
      };
      const rest = () => {
        const delay = pending ? 120 : COOLDOWN;
        nextBurstAt = performance.now() + delay;
        phase('cooldown');
        later(burst, delay);
      };
      function burst() {
        if (!runnable()) return;
        pending = false;
        nextBurstAt = performance.now() + CYCLE + COOLDOWN;
        const beat = (second = false) => {
          phase('burst');
          later(() => {
            if (!second) {
              phase('between');
              later(() => beat(true), BEAT_GAP);
            } else {
              phase('relax');
              later(rest, RELAX);
            }
          }, BURST);
        };
        beat();
      }
      const request = () => {
        if (!runnable()) return;
        const now = performance.now();
        if (now - lastInteraction < 100) return;
        lastInteraction = now;
        if (this.dataset.signalPhase === 'idle' || this.dataset.signalPhase === 'cooldown') {
          stop();
          burst();
        } else {
          // Retain one extra pair; repeated movement never restarts a running sweep.
          pending = true;
        }
      };
      const enter = event => {
        if (event.pointerType === 'touch') return;
        pointerX = event.clientX;
        pointerY = event.clientY;
        request();
      };
      const move = event => {
        if (event.pointerType === 'touch' || Math.hypot(event.clientX - pointerX, event.clientY - pointerY) < 4) return;
        pointerX = event.clientX;
        pointerY = event.clientY;
        request();
      };
      const sync = () => {
        stop();
        pending = false;
        lastInteraction = -Infinity;
        if (disposed) return;
        if (!runnable()) {
          phase(motion.matches ? 'still' : 'idle');
          return;
        }
        // Returning to view must not turn the four-second rest into a new burst.
        const remaining = nextBurstAt - performance.now();
        phase(remaining > 0 ? 'cooldown' : 'idle');
        later(burst, Math.max(ENTRANCE, remaining));
      };
      const observer = new IntersectionObserver(entries => {
        if (disposed) return;
        const next = entries.some(entry => entry.isIntersecting);
        if (next !== visible) { visible = next; sync(); }
      }, { threshold: 0 });
      observer.observe(this);
      document.addEventListener('visibilitychange', sync);
      motion.addEventListener('change', sync);
      surface.addEventListener('pointerenter', enter);
      surface.addEventListener('pointermove', move);
      this.dispose = () => {
        disposed = true;
        stop();
        observer.disconnect();
        document.removeEventListener('visibilitychange', sync);
        motion.removeEventListener('change', sync);
        surface.removeEventListener('pointerenter', enter);
        surface.removeEventListener('pointermove', move);
        phase('idle');
      };
      sync();
    }

    disconnectedCallback() {
      this.dispose?.();
      this.dispose = null;
    }
  }

  customElements.define('product-stock-signal', ProductStockSignal);
})();
